import { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

import { Button } from '@/components/Button/Button';

import { supportErrorMessage } from './supportConsoleFormat';
import styles from './RemoveStudentDialog.module.css';

// Who is about to be removed from what.
export interface RemoveStudentTarget {
  courseId: string;
  courseName: string;
  userName: string;
}

interface RemoveStudentDialogProps {
  // Where focus goes once the student has been removed. The button that opened
  // the pop-up is gone by then: its row left the table.
  focusAfterRemoval(): void;
  // Cancel, Escape or a click outside: nobody is removed.
  onClose(): void;
  onRemove(target: RemoveStudentTarget): Promise<void>;
  onRemoved(target: RemoveStudentTarget): void;
  // Null keeps the pop-up closed.
  target: RemoveStudentTarget | null;
}

// Asks before a student is removed from a course. The repo's confirmation
// shape (see StaffRemoveConfirmation): the alertdialog role, focus on the safe
// choice, an outlined danger button.
//
// The user panel owns this, not the course's row: the row leaves the table as
// the user reloads, and a pop-up inside it would vanish mid-flow and drop
// focus to the page.
export function RemoveStudentDialog({
  focusAfterRemoval,
  onClose,
  onRemove,
  onRemoved,
  target,
}: RemoveStudentDialogProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const cancelRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const removedRef = useRef(false);

  // A fresh start for each removal asked about.
  useEffect(() => {
    setBusy(false);
    setError('');
  }, [target]);

  async function handleRemove() {
    if (!target || busy) {
      return;
    }

    setBusy(true);
    setError('');
    try {
      await onRemove(target);
    } catch (removeError) {
      setError(supportErrorMessage(removeError));
      setBusy(false);
      return;
    }

    removedRef.current = true;
    onRemoved(target);
  }

  return (
    <Dialog.Root
      onOpenChange={(nextOpen) => {
        // Not while the request is out: its answer needs somewhere to land.
        if (!nextOpen && !busy) {
          onClose();
        }
      }}
      open={target !== null}
    >
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content
          className={styles.dialog}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            const removed = removedRef.current;
            removedRef.current = false;

            if (removed) {
              focusAfterRemoval();
            } else if (returnFocusRef.current?.isConnected) {
              returnFocusRef.current.focus();
            }
          }}
          onOpenAutoFocus={(event) => {
            // Focus has not moved yet: this is the button that opened the pop-up.
            returnFocusRef.current =
              document.activeElement instanceof HTMLElement ? document.activeElement : null;
            event.preventDefault();
            cancelRef.current?.focus();
          }}
          role="alertdialog"
        >
          <div>
            <Dialog.Title className={styles.title}>
              Remove {target?.userName} from {target?.courseName}?
            </Dialog.Title>
            <Dialog.Description className={styles.text}>
              The course comes off their course list, and they come off the course&apos;s
              roster. Their saved work is kept: if they are added to the course again, it
              is all still there.
            </Dialog.Description>
          </div>

          {error ? (
            <p className={styles.error} role="alert">
              {error}
            </p>
          ) : null}

          <div className={styles.footer}>
            <Button disabled={busy} onClick={onClose} ref={cancelRef} variant="secondary">
              Cancel
            </Button>
            {/* Outlined, not filled: the filled danger button's white text
                falls short of 4.5:1 in the dark theme. */}
            <Button
              destructive
              disabled={busy}
              onClick={() => void handleRemove()}
              variant="secondary"
            >
              {busy ? 'Removing…' : 'Remove'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
